import { CloseOutlined } from '@ant-design/icons';
import { Button } from '@mm/components';
import { useCampaignCRUDActions } from '@mm/state/actions/CampaignsCRUDActions';
import { useCampaignsOverviewCRUDActions } from '@mm/state/actions/CampaignsOverviewCRUDActions';
import { useModalActions } from '@mm/state/modules/modalsState/actions';
import {
  activeFacebookPageSelector,
  selectAccessiblePagesByRoles,
} from '@mm/state/selectors/facebookPageSelector';
import { activeChatbotIdSelector } from '@mm/state/selectors/viewSelectors';
import classNames from 'classnames';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ModalInstanceProps } from '../../../../../../../common/modals/interfaces';
import { CloseModalProps } from '../../../../../../../common/modals/modal-helpers';
import { InputWithClipboard } from '../../../../../automation/common/InputWithClipboard/InputWithClipboard';
import crystalIcon from './Icons/crystal.svg';
import animatedFolder from './Icons/animated-folder.gif';
import css from './CloneAutomationModal.module.css';
import { ConnectedPagesSelector } from './ConnectedPagesSelector/ConnectedPagesSelector';
import { StoreState } from '@mm/state/store-interfaces';
import { unbusyAll } from '@mm/state/actions/commonActions';
import { ExplainerButton } from './ExplainerButton/ExplainerButton';
import { Radio } from 'antd';

type Props = CloseModalProps &
  ModalInstanceProps & {
    campaignId: number;
    showCongratulation?: boolean;
  };

export const Congratulation = ({
  showCopyLaterText,
  closeModal,
}: {
  showCopyLaterText: boolean;
  closeModal: () => void;
}) => {
  const headerText = showCopyLaterText
    ? 'We are copying your Automation'
    : 'Success';
  const subTitleText = showCopyLaterText
    ? 'It takes up to 3 minutes'
    : 'Your Automation was successfully copied';
  const iconSrc = showCopyLaterText ? animatedFolder : crystalIcon;
  return (
    <>
      <span className={css.close} onClick={closeModal}>
        <CloseOutlined />
      </span>
      <div className={css.congratsContent}>
        <img src={iconSrc} className={css.congratsIcon} />
        <div
          className={classNames(css.title, {
            [css.loading]: showCopyLaterText,
          })}
        >
          {headerText}
        </div>
        <div className={css.subTitle}>{subTitleText}</div>
      </div>
      ;
    </>
  );
};

export const CloneAutomationModal = (props: Props) => {
  const { campaignId, closeModal, showCongratulation } = props;
  const activeFacebookPage = useSelector(activeFacebookPageSelector);
  const chatbotId = useSelector(activeChatbotIdSelector);
  const [searchToken, setSearchToken] = useState('');
  const [selectedPageIds, setSelectedPageIds] = useState([]);
  const [showExplainer, setShowExplainer] = useState(false);
  const campaignsCRUDActions = useCampaignCRUDActions();
  const campaignsOverviewCRUDActions = useCampaignsOverviewCRUDActions();
  const { openCloneAutomationModal } = useModalActions();
  const busy = useSelector((state: StoreState) => state.campaigns.busy);
  const dispatch = useDispatch();
  const ref = useRef<HTMLDivElement>(null);
  const [computedHeight, setComputedHeight] = useState('333px');
  const accessiblePages = useSelector(selectAccessiblePagesByRoles);

  const [cloneOption, setCloneOption] = useState<
    'thisAccount' | 'otherAccount' | 'shareCode'
  >('thisAccount');

  const fetchCampaigns = useCallback(async () => {
    await campaignsOverviewCRUDActions.retrieveAll({
      botId: activeFacebookPage?.activeBotId || chatbotId,
      kind: 'automation',
      pagination: {
        page: 1,
        perPage: 8,
      },
    });
  }, [
    activeFacebookPage?.activeBotId,
    campaignsOverviewCRUDActions,
    chatbotId,
  ]);

  useEffect(() => {
    if (ref?.current) {
      const styles = window.getComputedStyle(ref?.current);
      setComputedHeight(styles.height);
    }
  }, [cloneOption, accessiblePages]);

  const handleSubmit = useCallback(async () => {
    try {
      if (cloneOption === 'shareCode') {
        closeModal();
      }
      if (cloneOption === 'thisAccount') {
        openCloneAutomationModal({
          campaignId,
          showCongratulation: true,
        });
        await campaignsCRUDActions.clone(campaignId);
        fetchCampaigns();
      }
      if (cloneOption === 'otherAccount') {
        openCloneAutomationModal({
          campaignId,
          showCongratulation: true,
        });
        await campaignsCRUDActions.clone(
          campaignId,
          selectedPageIds?.length ? selectedPageIds : undefined,
        );
        fetchCampaigns();
      }
    } catch (err) {}
  }, [
    cloneOption,
    closeModal,
    campaignsCRUDActions,
    campaignId,
    selectedPageIds,
    openCloneAutomationModal,
    fetchCampaigns,
  ]);

  const retrieveSearchToken = useCallback(async () => {
    const response = await campaignsCRUDActions.retrieveCampaignSearchToken(
      campaignId,
    );
    if (response) {
      setSearchToken(response.searchToken);
    }
  }, [campaignId, campaignsCRUDActions]);

  return (
    <div
      className={classNames(
        !showCongratulation ? css.container : css.congratsContainer,
      )}
    >
      {showCongratulation && (
        <Congratulation
          closeModal={() => {
            closeModal();
            dispatch(unbusyAll());
          }}
          showCopyLaterText={busy}
        />
      )}
      {!showCongratulation && (
        <div className={css.modal}>
          <span className={css.close} onClick={closeModal}>
            <CloseOutlined />
          </span>

          <div
            ref={ref}
            className={classNames(css.content)}
            style={showExplainer ? { height: computedHeight } : {}}
          >
            <div className={css.title}>
              Clone Automation{' '}
              <ExplainerButton
                className={css.explainerButton}
                buttonName="What is not cloned?"
                showExplainer={showExplainer}
                setShowExplainer={setShowExplainer}
              />
            </div>
            {!showExplainer && (
              <>
                <div className={css.body}>
                  <div className={css.radioInpuWrapper}>
                    <Radio
                      className={css.radioButton}
                      name="thisAccount"
                      checked={cloneOption === 'thisAccount'}
                      onChange={() => setCloneOption('thisAccount')}
                      value="cloneOption"
                    >
                      Clone to this account
                    </Radio>
                  </div>
                  <div className={css.radioInpuWrapper}>
                    <Radio
                      className={css.radioButton}
                      name="otherAccount"
                      checked={cloneOption === 'otherAccount'}
                      onChange={() => setCloneOption('otherAccount')}
                      value="cloneOption"
                    >
                      Clone to another account I manage
                    </Radio>
                    {cloneOption === 'otherAccount' && (
                      <ConnectedPagesSelector
                        className={css.pagesSelector}
                        selectedPageIds={selectedPageIds}
                        setSelectedPageIds={setSelectedPageIds}
                      />
                    )}
                  </div>
                  <div className={css.radioInpuWrapper}>
                    <div className={css.radioButtonWithHelperContainer}>
                      <Radio
                        className={css.radioButton}
                        name="shareCode"
                        checked={cloneOption === 'shareCode'}
                        onChange={() => {
                          setCloneOption('shareCode');
                          retrieveSearchToken();
                        }}
                        value="cloneOption"
                      >
                        Share externally
                      </Radio>

                      <div className={css.helper}>
                        (For your security, Integrations credentials will not be
                        shared)
                      </div>
                    </div>
                    {cloneOption === 'shareCode' && (
                      <InputWithClipboard
                        buttonName="Copy Token"
                        title="Send the token below to another Customers.ai user for import"
                        value={searchToken}
                        className={css.shareCodeWrapper}
                      />
                    )}
                  </div>
                </div>

                <div className={css.buttonsWrapper}>
                  <Button
                    color="transparent-no-shadow"
                    css={{ container: css.cancelButton }}
                    onClick={closeModal}
                  >
                    Cancel
                  </Button>

                  <Button
                    css={{ container: css.actionButton }}
                    disabled={
                      busy ||
                      (cloneOption === 'otherAccount' &&
                        !selectedPageIds.length)
                    }
                    onClick={handleSubmit}
                  >
                    Continue
                  </Button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
